import { useDebounce } from "@/utils";
import type { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import { useAtom } from "jotai";
import { atomFamily, atomWithStorage, createJSONStorage } from "jotai/utils";
import { useCallback, useMemo, useState } from "react";

type Options = {
	initialSorting?: SortingState;
	initialFilters?: ColumnFiltersState;
	initialGlobalFilter?: string;
	initialPageSize?: number;
};

const storage = createJSONStorage<number>(() => localStorage);
const paginationStorage = {
	...storage,
	getItem: (key: string, initialValue: number) => {
		try {
			if (typeof window === "undefined") return initialValue;
			return storage.getItem(key, initialValue);
		} catch {
			return initialValue;
		}
	},
};
const pageAtom = atomFamily((key: string) =>
	atomWithStorage(`pagination:${key}`, 1, paginationStorage, {
		getOnInit: true,
	}),
);

export function usePersistedServerTableState(
	key: string,
	{
		initialSorting = [],
		initialFilters = [],
		initialGlobalFilter = "",
		initialPageSize = 10,
	}: Options = {},
) {
	const [page, setPage] = useAtom(pageAtom(key));
	const [pageSize, setPageSize] = useState(initialPageSize);
	const [sorting, setSorting] = useState(initialSorting);
	const [columnFilters, setColumnFilters] = useState(initialFilters);
	const [globalFilter, setGlobalFilter] = useState(initialGlobalFilter);
	const debouncedGlobalFilter = useDebounce(globalFilter, 400);
	const safePage = Number.isInteger(page) && page > 0 ? page : 1;

	const onPageChange = useCallback(
		(nextPage: number) => setPage(Math.max(1, nextPage)),
		[setPage],
	);
	const onPageSizeChange = useCallback(
		(size: number) => {
			setPageSize(Math.max(1, size));
			setPage(1);
		},
		[setPage],
	);
	const onSortingChange = useCallback(
		(updater: React.SetStateAction<SortingState>) => {
			setSorting(updater);
			setPage(1);
		},
		[setPage],
	);
	const onColumnFiltersChange = useCallback(
		(updater: React.SetStateAction<ColumnFiltersState>) => {
			setColumnFilters(updater);
			setPage(1);
		},
		[setPage],
	);
	const onGlobalFilterChange = useCallback(
		(value: string) => {
			setGlobalFilter(value);
			setPage(1);
		},
		[setPage],
	);

	const queryInput = useMemo(
		() => ({
			page: safePage,
			pageSize,
			sort: sorting.map(({ id, desc }) => ({ id, desc: desc ?? false })),
			filters: columnFilters.map(({ id, value }) => ({ id, value })),
			q: debouncedGlobalFilter || undefined,
		}),
		[safePage, pageSize, sorting, columnFilters, debouncedGlobalFilter],
	);

	return {
		pageIndex: safePage - 1,
		pageSize,
		sorting,
		columnFilters,
		globalFilter,
		onSortingChange,
		onColumnFiltersChange,
		onGlobalFilterChange,
		onPageChange,
		onPageSizeChange,
		queryInput,
	};
}
